import { t } from '../../shared/i18n.js';
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { TILE } from '../../shared/geo.js';
import { hash2 } from '../../shared/rng.js';


const POST_SHARE = 0.06; // share of map tiles with a main road that have a control post

const QUEUE = 6; // vehicles waiting at a fuel station when fuel is short

function colored(geometry, hex) {
  const g = geometry.index ? geometry.toNonIndexed() : geometry;
  const c = new THREE.Color(hex);
  const n = g.attributes.position.count;
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    col[i * 3] = c.r;
    col[i * 3 + 1] = c.g;
    col[i * 3 + 2] = c.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}
const box = (w, h, d, hex, x, y, z) => colored(new THREE.BoxGeometry(w, h, d).translate(x, y, z), hex);
const drum = (x, z) => [
  colored(new THREE.CylinderGeometry(0.3, 0.3, 0.3, 10).translate(x, 0.15, z), '#c8402f'),
  colored(new THREE.CylinderGeometry(0.3, 0.3, 0.3, 10).translate(x, 0.45, z), '#f1eee6'),
  colored(new THREE.CylinderGeometry(0.3, 0.3, 0.3, 10).translate(x, 0.75, z), '#c8402f'),
];

// Things by the roadside that belong to present-day life, shown without
// comment: control posts on the main roads, where everything slows down and
// drivers show their papers, and queues at the fuel stations when fuel is short.
export class Roadside {
  // traffic: the game's Traffic (it knows the roads). vehicles: to stand a vehicle on sloping ground.
  constructor(scene, map, traffic, vehicles, hud) {
    this.scene = scene;
    this.map = map;
    this.traffic = traffic;
    this.vehicles = vehicles;
    this.hud = hud;
    this.material = new THREE.MeshLambertMaterial({ vertexColors: true });
    this.posts = new Map(); // tile key -> { x, z, mesh, told }
    this.stations = new Map(); // tile key -> [{ n, x, z, queue: [meshes] }]
    this.short = false;
    this.fuel = map.places.filter((p) => p.k === 'fuel');
    traffic.slow = []; // the posts, for the traffic to crawl past
  }

  // Call when a tile is built, after the traffic has been given it.
  addTile(t) {
    if (!t.data) return;
    const ox = t.tx * TILE;
    const oz = t.tz * TILE;
    // a control post on a main road, in a few tiles chosen by the map itself so everyone has the same
    const main = t.data.r.find((r) => r.c <= 1 && r.p.length >= 6);
    if (main && hash2(t.tx, t.tz, 91) < POST_SHARE) {
      const k = Math.floor(main.p.length / 4) * 2;
      const x = ox + main.p[k] / 10;
      const z = oz + main.p[k + 1] / 10;
      const dx = main.p[k + 2] - main.p[k];
      const dz = main.p[k + 3] - main.p[k + 1];
      const yaw = Math.atan2(dx, dz);
      const half = main.w / 2;
      const parts = [
        // drums narrowing the road from both sides, a little apart along it
        ...drum(half - 0.5, -3),
        ...drum(-half + 0.5, 3),
        ...drum(half + 0.4, -3),
        ...drum(-half - 0.4, 3),
        // a zinc shelter with a bench beside the road
        box(0.1, 2.2, 0.1, '#5a4634', half + 1.6, 1.1, -1.2),
        box(0.1, 2.2, 0.1, '#5a4634', half + 1.6, 1.1, 1.2),
        box(0.1, 2.2, 0.1, '#5a4634', half + 3.4, 1.1, -1.2),
        box(0.1, 2.2, 0.1, '#5a4634', half + 3.4, 1.1, 1.2),
        box(2.4, 0.08, 3, '#a9aeb2', half + 2.5, 2.24, 0),
        box(0.4, 0.08, 2, '#7a5230', half + 3, 0.5, 0),
        // the sign
        box(0.1, 1.6, 0.1, '#3a3a3e', half + 1, 0.8, -4.5),
        box(0.9, 0.6, 0.06, '#f1eee6', half + 1, 1.7, -4.5),
        box(0.7, 0.14, 0.07, '#c8402f', half + 1, 1.72, -4.5),
      ];
      const mesh = new THREE.Mesh(mergeGeometries(parts), this.material);
      mesh.position.set(x, this.map.heightmap.heightAt(x, z), z);
      mesh.rotation.y = yaw;
      mesh.castShadow = true;
      this.scene.add(mesh);
      this.posts.set(t.key, { x, z, mesh, told: 0 });
      this.traffic.slow.push(this.posts.get(t.key));
    }
    const here = this.fuel.filter((p) => Math.floor(p.x / TILE) === t.tx && Math.floor(p.z / TILE) === t.tz);
    if (here.length) {
      this.stations.set(t.key, here.map((p) => ({ n: p.n, x: p.x, z: p.z, queue: null, told: false })));
      if (this.short) for (const s of this.stations.get(t.key)) this.line(s, t.key);
    }
  }

  removeTile(t) {
    const post = this.posts.get(t.key);
    if (post) {
      this.scene.remove(post.mesh);
      post.mesh.geometry.dispose();
      this.posts.delete(t.key);
      this.traffic.slow.splice(this.traffic.slow.indexOf(post), 1);
    }
    for (const s of this.stations.get(t.key) || []) this.clear(s);
    this.stations.delete(t.key);
  }

  clear(station) {
    if (!station.queue) return;
    for (const v of station.queue) this.vehicles.pool[v.type].remove(v.slot);
    station.queue = null;
  }

  // Lines vehicles up along the road nearest a station, nose to tail, waiting for fuel.
  line(station, key) {
    if (station.queue) return;
    let best = null;
    let bestD = 60 * 60;
    for (const road of this.traffic.roads.get(key) || []) {
      for (let k = 0; k + 1 < road.n; k++) {
        const d = (road.pts[k * 2] - station.x) ** 2 + (road.pts[k * 2 + 1] - station.z) ** 2;
        if (d < bestD) {
          bestD = d;
          best = { road, k };
        }
      }
    }
    station.queue = [];
    if (!best) return;
    const { road, k } = best;
    const ax = road.pts[k * 2];
    const az = road.pts[k * 2 + 1];
    const ex = road.pts[k * 2 + 2] - ax;
    const ez = road.pts[k * 2 + 3] - az;
    const len = Math.hypot(ex, ez) || 1;
    const fx = ex / len;
    const fz = ez / len;
    const side = road.w / 2 + 1.3; // on the verge, off the carriageway
    let back = 0;
    for (let i = 0; i < QUEUE; i++) {
      const type = hash2(Math.round(station.x), i, 93) < 0.45 ? 'bike' : 'taxi';
      const v = { type, x: ax - fx * back - fz * side, z: az - fz * back + fx * side, yaw: Math.atan2(fx, fz), slot: this.vehicles.pool[type].add() };
      this.vehicles.place(v);
      station.queue.push(v);
      back += type === 'taxi' ? 5.4 : 2.6;
    }
  }

  // short: whether fuel is short in the region today, as the server says.
  setFuel(short) {
    if (short === this.short) return;
    this.short = short;
    for (const [key, list] of this.stations) {
      for (const s of list) {
        if (short) this.line(s, key);
        else this.clear(s);
        s.told = false;
      }
    }
    this.hud.toast(short ? t('Fuel is short today: there are queues at the stations') : t('Fuel has come: the queues at the stations are gone'), 5000);
  }

  // Call every frame. car: what the player is driving, or null. t: milliseconds.
  update(car, x, z, t) {
    if (!car) return;
    for (const post of this.posts.values()) {
      const d = Math.hypot(post.x - x, post.z - z);
      if (d > 30 || t - post.told < 60000) continue;
      const speed = Math.abs(car.speed);
      if (d < 12 && speed < 1.5) {
        post.told = t;
        this.hud.toast('You show your ID card and are waved on', 3500);
      } else if (speed > 9) {
        post.told = t - 52000; // may be said again in a few seconds if they do not slow
        this.hud.toast('A control post: slow down and stop', 3000);
      }
    }
  }
}

